// LivingCanvas — Milestone 1: the Brain at REST, before anyone touches anything.
//
// It renders the immersive substrate, the one bright priority, real temporal depth, and
// Cipher's first-person voice — and NOTHING clicks, expands, or executes (NeuralGraph is
// mounted non-interactive; there are zero affordances). The only motion at rest is the
// single priority pulse + real temporal decay (Bible Law #1). The "watch it think"
// surprise is honest polling of existing read endpoints; a node brightens ONLY on a real
// delta (Bible §5) — never simulated.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useBrainResource } from "./lib/hooks";
import { buildDecisionGraph } from "./lib/brainGraphAdapter";
import { selectExecutionRec } from "./lib/brainState";
import { temporalTreatment, recencySeconds } from "./lib/temporal";
import { deriveVoiceLines, collectRealMoments } from "./lib/voice";
import { snapshotSignals, detectPollDeltas, brightenTargets } from "./lib/poll";
import { makeAttentionModel } from "./lib/attention";
import { motion } from "./lib/motion";
import { useReducedMotion } from "./lib/useReducedMotion";
import NeuralGraph from "./NeuralGraph";
import CipherVoice from "./CipherVoice";

const POLL_MS = 45000; // calm cadence

function listOf(res, key) {
  if (!res || res.status !== "ok" || !res.data) return [];
  return res.data[key] || (Array.isArray(res.data) ? res.data : []);
}

export default function LivingCanvas() {
  const reduce = useReducedMotion();

  // Attention-model FOUNDATION built here (M1 surfaces no interactions).
  const attentionRef = useRef(makeAttentionModel());

  // Structural reads (the mind's already-loaded state).
  const queue = useBrainResource("/executive/queue", { params: { limit: 25 } });
  const outcomes = useBrainResource("/outcomes");
  const goals = useBrainResource("/goals");
  const predsActive = useBrainResource("/predictions/active");
  const observations = useBrainResource("/intelligence/observations");
  const journalEntries = useBrainResource("/journal");
  const evaluations = useBrainResource("/evaluations");

  const [now, setNow] = useState(() => Date.now());
  const [brightenIds, setBrightenIds] = useState(() => new Set());
  const [announce, setAnnounce] = useState("");
  const prevSnap = useRef(null);
  const brightenTimer = useRef(null);

  const items = listOf(queue, "items");
  const priority = useMemo(() => selectExecutionRec(queue.data), [queue.data]);
  const priorityNodeId = priority ? `decision:${priority.item_id}` : null;

  const graph = useMemo(
    () =>
      buildDecisionGraph({
        decisions: items,
        outcomes: listOf(outcomes, "outcomes"),
        goals: listOf(goals, "goals"),
        predictions: listOf(predsActive, "predictions"),
        observations: listOf(observations, "observations"),
        journalEntries: listOf(journalEntries, "entries"),
      }),
    [items, outcomes.data, goals.data, predsActive.data, observations.data, journalEntries.data] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Real recency per node ref (only where a real timestamp exists).
  const recencyByRef = useMemo(() => {
    const m = {};
    for (const o of listOf(outcomes, "outcomes")) if (o.outcome_id) m[o.outcome_id] = o.created_at || o.resolved_at || o.opened_at;
    for (const o of listOf(observations, "observations")) { const id = o.observation_id || o.id; if (id) m[id] = o.created_at; }
    for (const e of listOf(journalEntries, "entries")) if (e.entry_id) m[e.entry_id] = e.created_at;
    return m;
  }, [outcomes.data, observations.data, journalEntries.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Depth + lighting + temporal treatment per node. Light = attention: priority
  // brightest/forward, periphery dim/receded. Temporal warmth/tier ONLY from real recency.
  const nodeTreatments = useMemo(() => {
    const map = {};
    for (const n of graph.nodes || []) {
      const treat = temporalTreatment(recencySeconds(recencyByRef[n.refId], now));
      let luminance = treat.hasCue ? treat.luminance : 0.55; // no cue => periphery dim (attention model, not a temporal claim)
      let depth = treat.hasCue ? treat.depth : 0.35;
      if (n.id === priorityNodeId) { luminance = 1; depth = 0; }
      map[n.id] = { luminance, warmth: treat.warmth, depth, tier: treat.tier };
    }
    return map;
  }, [graph.nodes, recencyByRef, now, priorityNodeId]);

  // Cipher's voice from REAL moments.
  const labels = useMemo(() => {
    const l = {};
    for (const it of items) if (it.item_id) l[it.item_id] = it.title || it.item_id;
    return l;
  }, [items]);

  const voice = useMemo(() => {
    const moments = collectRealMoments({ outcomes: listOf(outcomes, "outcomes"), evaluations: listOf(evaluations, "evaluations"), journalEvents: [] });
    return deriveVoiceLines({ ...moments, priority, pendingCount: items.length, labels });
  }, [outcomes.data, evaluations.data, priority, items.length, labels]); // eslint-disable-line react-hooks/exhaustive-deps

  // Honest polling: diff snapshots of already-loaded reads; brighten ONLY on a real delta.
  // The FIRST settled snapshot is a silent baseline — surprise never fires on initial load
  // (awaken handles load). Only genuine changes after baseline brighten a node.
  useEffect(() => {
    if (outcomes.status === "loading" || observations.status === "loading" || evaluations.status === "loading") return;
    const snap = snapshotSignals({
      outcomes: listOf(outcomes, "outcomes"),
      observations: listOf(observations, "observations"),
      evaluations: listOf(evaluations, "evaluations"),
    });
    if (prevSnap.current == null) { prevSnap.current = snap; return; } // silent baseline
    const { deltas } = detectPollDeltas(prevSnap.current, snap);
    prevSnap.current = snap;
    if (deltas.length === 0) return;
    const targets = brightenTargets(deltas);
    if (targets.length) {
      setBrightenIds(new Set(targets));
      if (brightenTimer.current) clearTimeout(brightenTimer.current);
      brightenTimer.current = setTimeout(() => setBrightenIds(new Set()), motion("surprise-brighten").durationMs + 200);
    }
    setAnnounce(`Cipher just took in ${deltas.length} new signal${deltas.length === 1 ? "" : "s"}.`);
  }, [outcomes.status, outcomes.data, observations.status, observations.data, evaluations.status, evaluations.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calm poll cadence — re-read the existing endpoints; the effect above detects deltas.
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return; // pause when unseen
      setNow(Date.now()); // advance temporal cooling
      outcomes.reload();
      observations.reload();
      evaluations.reload();
    }, POLL_MS);
    return () => {
      clearInterval(id);
      if (brightenTimer.current) clearTimeout(brightenTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- poll intentionally keyed on stable .reload fns, not the live objects
  }, [outcomes.reload, observations.reload, evaluations.reload]);

  const awaken = motion("awaken", { reducedMotion: reduce });
  const loading = queue.status === "loading";

  return (
    <div
      data-testid="living-canvas"
      className="relative min-h-[70vh]"
      style={{ opacity: reduce ? 1 : undefined, animation: reduce ? "none" : `brainAwaken ${awaken.cssDuration} ${awaken.cssEasing} both` }}
    >
      {/* one-shot awaken keyframes (environment transition, not ambient) */}
      <style>{`@keyframes brainAwaken { from { opacity: 0 } to { opacity: 1 } }`}</style>

      <div className="px-1 py-2">
        <CipherVoice voice={loading ? null : voice} loading={loading} />
      </div>

      <div className="mt-4">
        <NeuralGraph
          graph={graph}
          interactive={false}
          priorityNodeId={priorityNodeId}
          nodeTreatments={nodeTreatments}
          brightenIds={brightenIds}
          height={560}
        />
      </div>

      {/* surprise announcement — reaches SR even under reduced motion (Bible §5 fallback) */}
      <p className="sr-only" role="status" aria-live="polite" data-testid="surprise-announce">{announce}</p>
    </div>
  );
}
